import React, { ReactElement, useState } from 'react';
import { useApiRequest } from 'src/hooks/useApi';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import {
	Typography,
	Box,
	Switch,
	SwitchLabel,
	BDSProvider,
	Alert,
	Grid,
} from '@barracuda-internal/bds-core-v2';
import useAuth from 'src/hooks/useAuth';
import { useLocation } from 'react-router-dom';
import FEATURES from 'src/featureMap';
import mixpanel from 'mixpanel-browser';
import { version } from '../../../package.json';

function RequestMalwareScanningConsent(): ReactElement {
	const [consent, setConsent] = useState<boolean>(false);
	const [error, setError] = useState<boolean>(false);
	const { api } = useAppToolchain();
	const { auth } = useAuth();
	const location = useLocation();

	useApiRequest(
		(_) => api.getAccountConfig(auth?.accountDetails.account.id),
		[auth?.accountDetails.account.id],
		(resp) => {
			const account = resp[1];
			setConsent(account?.enableMalwareScan || false);
		},
	);

	const handleChange = async (
		e: React.ChangeEvent<HTMLInputElement>,
		checked: boolean,
	) => {
		setConsent(checked);
		setError(false);
		try {
			await api.saveAccountConfig(auth.accountDetails.account.id, [
				{
					path: '/enableMalwareScan',
					op: 'replace',
					value: checked,
				},
			]);

			const action = FEATURES.settingsMalwareScanningToggle;
			mixpanel.track(`DIN ${action.id}`, {
				urlPath: location.pathname,
				'Malware Scan': checked ? 'enabled' : 'disabled',
				feature: action.feature,
				appVersion: version,
				'Account Id': auth?.accountDetails.account.id,
				'Account Name': auth?.accountDetails.account.name,
			});
		} catch (err) {
			setError(true);
		}
	};

	return (
		<BDSProvider>
			<Box>
				<Typography variant="h3" color="textPrimary" sx={{ mb: 4 }}>
					Enable Malware Scanning
				</Typography>

				{error && (
					<Alert severity="error">
						There was an error saving the form. Please try again.
					</Alert>
				)}

				<Box sx={{ ml: 2, mt: 2 }}>
					<Grid
						container
						spacing={1}
						style={{ alignItems: 'center', display: 'flex', marginLeft: '4px' }}
					>
						<Grid item xs={3}>
							<SwitchLabel
								sx={{ gap: 1 }}
								control={
									<Switch
										name="checkedB"
										color="primary"
										onChange={handleChange}
										checked={consent}
									/>
								}
								label={consent ? 'Enabled' : 'Enable'}
							/>
						</Grid>
						<Grid item xs={9}>
							<Typography variant="body1" color="textPrimary">
								Enable malware scanning to detect malicious
								files.
							</Typography>
						</Grid>
					</Grid>
				</Box>
			</Box>
		</BDSProvider>
	);
}

export default RequestMalwareScanningConsent;
